'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { IconClose } from '../chatbot/icons';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn(
			'lb-fixed lb-inset-0 lb-z-50 lb-bg-black/80 lb- data-[state=open]:lb-animate-in data-[state=closed]:lb-animate-out data-[state=closed]:lb-fade-out-0 data-[state=open]:lb-fade-in-0',
			className
		)}
		{...props}
	/>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
	<DialogPortal>
		<DialogOverlay />
		<DialogPrimitive.Content
			ref={ref}
			className={cn(
				'lb-fixed lb-left-[50%] lb-top-[50%] lb-z-50 lb-flex lb-flex-col lb-w-full lb-max-w-lg lb-translate-x-[-50%] lb-translate-y-[-50%] lb-gap-4 lb-border lb-border-solid lb-border-border lb-bg-background lb-p-6 lb-shadow-lg lb-duration-200 data-[state=open]:lb-animate-in data-[state=closed]:lb-animate-out data-[state=closed]:lb-fade-out-0 data-[state=open]:lb-fade-in-0 data-[state=closed]:lb-zoom-out-95 data-[state=open]:lb-zoom-in-95 data-[state=closed]:lb-slide-out-to-left-1/2 data-[state=closed]:lb-slide-out-to-top-[48%] data-[state=open]:lb-slide-in-from-left-1/2 data-[state=open]:lb-slide-in-from-top-[48%] sm:lb-rounded-lg',
				className
			)}
			{...props}
		>
			{children}
			<DialogPrimitive.Close className="lb-absolute lb-right-4 lb-top-4 lb-border-none !lb-bg-transparent lb-rounded-sm lb-opacity-70 lb-ring-offset-background lb-transition-opacity hover:lb-opacity-100 focus:lb-outline-none focus:lb-ring-0 disabled:lb-pointer-events-none data-[state=open]:lb-bg-accent data-[state=open]:lb-text-muted-foreground lb-cursor-pointer">
				<IconClose />
				<span className="lb-sr-only">Close</span>
			</DialogPrimitive.Close>
		</DialogPrimitive.Content>
	</DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			'lb-flex lb-flex-col lb-space-y-1.5 lb-text-center sm:lb-text-left',
			className
		)}
		{...props}
	/>
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			'lb-flex lb-flex-col-reverse sm:lb-flex-row sm:lb-justify-end sm:lb-space-x-2',
			className
		)}
		{...props}
	/>
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn(
			'lb-text-lg lb-font-semibold lb-leading-none lb-tracking-tight',
			className
		)}
		{...props}
	/>
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description
		ref={ref}
		className={cn('lb-text-sm lb-text-muted-foreground', className)}
		{...props}
	/>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
	Dialog,
	DialogPortal,
	DialogOverlay,
	DialogTrigger,
	DialogClose,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogTitle,
	DialogDescription
};
